import { useCallback, useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { ArrowLeft, Play, Plus, Pencil, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { toast } from "sonner"
import { ROUTES } from "@/constants/routes"
import {
  fetchCards, createCard, updateCard, deleteCard, deleteDeck,
  type CardItem,
} from "@/api/flashcards"

const STATUS_LABELS: Record<string, string> = {
  new: "新卡",
  learning: "学习中",
  review: "复习中",
  mastered: "已掌握",
}

interface CardFormState {
  cardId: string | null
  front: string
  back: string
  hint: string
}

export default function FlashcardDeckDetailPage() {
  const { deckId } = useParams<{ deckId: string }>()
  const navigate = useNavigate()

  const [items, setItems] = useState<CardItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const pageSize = 20
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [form, setForm] = useState<CardFormState | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    if (!deckId) return
    setLoading(true)
    setError(null)
    fetchCards({ deckId, page, pageSize })
      .then((res) => {
        setItems(res.items)
        setTotal(res.total)
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false))
  }, [deckId, page])

  useEffect(() => {
    load()
  }, [load])

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  async function handleSave() {
    if (!form || !deckId) return
    if (!form.front.trim() || !form.back.trim()) {
      toast.error("正面和背面内容不能为空")
      return
    }
    setSaving(true)
    try {
      if (form.cardId) {
        await updateCard({
          cardId: form.cardId,
          front: form.front,
          back: form.back,
          hint: form.hint || null,
        })
        toast.success("卡片已更新")
      } else {
        await createCard({
          deckId,
          front: form.front,
          back: form.back,
          hint: form.hint || undefined,
        })
        toast.success("卡片已添加")
      }
      setForm(null)
      load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "保存失败")
    } finally {
      setSaving(false)
    }
  }

  async function handleDeleteCard(card: CardItem) {
    if (!window.confirm(`确定删除这张卡片？\n正面：${card.front.slice(0, 50)}`)) return
    try {
      await deleteCard(card.id)
      toast.success("卡片已删除")
      load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "删除失败")
    }
  }

  async function handleDeleteDeck() {
    if (!deckId) return
    if (!window.confirm("确定删除整个卡组？")) return
    try {
      await deleteDeck(deckId)
      toast.success("卡组已删除")
      navigate(ROUTES.FLASHCARDS)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "删除失败")
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-4xl px-6 py-8 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to={ROUTES.FLASHCARDS}>
              <Button variant="ghost" size="icon"><ArrowLeft className="w-4 h-4" /></Button>
            </Link>
            <div>
              <h1 className="text-xl font-semibold">卡组详情</h1>
              <p className="text-sm text-slate-500">共 {total} 张卡片</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setForm({ cardId: null, front: "", back: "", hint: "" })}>
              <Plus className="w-4 h-4 mr-2" />
              添加卡片
            </Button>
            <Button onClick={() => navigate(`${ROUTES.FLASHCARD_STUDY}?deckId=${deckId}`)}>
              <Play className="w-4 h-4 mr-2" />
              开始学习
            </Button>
          </div>
        </div>

        {loading && <div className="text-sm text-slate-500">加载中...</div>}
        {error && <div className="text-sm text-red-500">{error}</div>}
        {!loading && !error && items.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center text-sm text-slate-500">
              卡组还是空的，点击右上角添加卡片
            </CardContent>
          </Card>
        )}

        <div className="space-y-3">
          {items.map((card) => (
            <Card key={card.id}>
              <CardContent className="py-3 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="text-sm font-medium break-words">{card.front}</div>
                  <div className="text-xs text-slate-500 break-words line-clamp-2">{card.back}</div>
                  <div className="flex items-center gap-2 pt-1">
                    {card.progress?.status && (
                      <Badge variant="secondary" className="text-xs">
                        {STATUS_LABELS[card.progress.status] ?? card.progress.status}
                      </Badge>
                    )}
                    {(card.tags ?? []).map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-slate-400 hover:text-blue-600"
                    onClick={() => setForm({
                      cardId: card.id,
                      front: card.front,
                      back: card.back,
                      hint: card.hint ?? "",
                    })}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-slate-400 hover:text-red-500"
                    onClick={() => handleDeleteCard(card)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              上一页
            </Button>
            <span className="text-sm text-slate-500">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              下一页
            </Button>
          </div>
        )}

        <div className="pt-2">
          <Button variant="ghost" className="text-red-500" onClick={handleDeleteDeck}>
            <Trash2 className="w-4 h-4 mr-2" />
            删除卡组
          </Button>
        </div>
      </div>

      {/* 添加/编辑卡片弹窗 */}
      <Dialog open={form !== null} onOpenChange={(open) => !open && setForm(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{form?.cardId ? "编辑卡片" : "添加卡片"}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-3">
              <Textarea
                placeholder="正面（问题/提示）"
                rows={3}
                value={form.front}
                onChange={(e) => setForm({ ...form, front: e.target.value })}
              />
              <Textarea
                placeholder="背面（答案/要点）"
                rows={4}
                value={form.back}
                onChange={(e) => setForm({ ...form, back: e.target.value })}
              />
              <Input
                placeholder="记忆线索（可选）"
                value={form.hint}
                onChange={(e) => setForm({ ...form, hint: e.target.value })}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>取消</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "保存中..." : "保存"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
